import { Search } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { UserService } from '../../../Service/UserService';
import { useAppDispatch } from '../../../store/hooks';
import { createNotification } from '../../../store/notifications/notification-slice';
import { User } from '../../../Utilis/Types/User';
import TrashIcon from '../../../assets/Icons/trash';
import { useSearchParams } from 'react-router-dom';
import { Meta } from '../../../Utilis/Types/Generic';
import { debounce } from 'lodash';
import { TableList } from '../../../components/TableList';
import { styles } from './styles';
import { useManagerModals } from './useManagerModals';

const useQuery = (defaultValues: {
  [key: string]: any;
}): [any, (v: any) => void] => {
  const [search, setSearchParams] = useSearchParams();

  const getQuery = () => {
    const query: any = Object.fromEntries(search.entries());

    for (const [key, value] of Object.entries(defaultValues)) {
      if (!query[key]) {
        query[key] = value;
      } else {
        query[key] = convertToTypeOf(typeof value, query[key]);
      }
    }

    return query;
  };

  const convertToTypeOf = (typedVar: any, input: any) => {
    if (typedVar === 'number') {
      return Number(input);
    } else {
      return String(input);
    }
  };

  return [getQuery(), setSearchParams];
};

export const AdminManagers = () => {
  const [query, setQuery] = useQuery({
    search: '',
    page: 1,
  });
  const appDispatch = useAppDispatch();
  const [users, setUsers] = useState<User[]>([]);
  const [meta, setMeta] = useState<Meta | undefined>(undefined);
  const { openCreateManagerModal, openRemoveManagerModal } = useManagerModals({
    setMeta,
    setUsers,
  });

  const queryParams = useMemo(() => {
    return { search: query?.search || '', page: Number(query?.page) || 1 };
  }, [query]);

  useEffect(() => {
    fetchMembers();
  }, [query?.search, query?.page]);

  const fetchMembers = async () => {
    try {
      const response = await UserService.getAllMembers(query);

      setUsers(response.data);
      setMeta(response.meta);
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong! Please try again later.',
        })
      );
    }
  };

  const searchUser = (e: any) => {
    e.preventDefault();
    const newQuery = { search: e.target.value, page: 1 };

    setQuery(newQuery);
  };

  const debounceSearch = debounce(searchUser, 500);

  const columns = [
    {
      field: 'profile',
      render: (data: string) => (
        <Box sx={styles.avatarContainer}>
          <Avatar src={data} sx={styles.avatar} />
        </Box>
      ),
    },
    {
      name: 'First Name',
      field: 'name',
    },
    {
      name: 'Last Name',
      field: 'lastName',
    },
    {
      name: 'Email address',
      field: 'email',
    },
    {
      field: 'delete',
      render: (data: any) => (
        <Box sx={{ padding: '0 30px' }}>
          <IconButton onClick={() => openRemoveManagerModal(data)}>
            <TrashIcon size={20} color='#475467' />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Box sx={styles.headerTitle}>
          <Typography fontWeight='bold'>Team members</Typography>
          <Typography sx={styles.label}>{meta?.itemCount} members</Typography>
        </Box>

        <Box sx={styles.inputContainer}>
          <TextField
            size='small'
            placeholder='Search members'
            sx={{ background: 'white' }}
            InputProps={{
              startAdornment: (
                <Search sx={{ marginRight: '5px ', color: '#667085' }} />
              ),
            }}
            defaultValue={queryParams.search}
            onChange={debounceSearch}
          />
          <Button variant='contained' onClick={openCreateManagerModal}>
            Create
          </Button>
        </Box>
      </Box>

      <TableList
        columns={columns}
        data={users}
        meta={meta}
        onPageChange={page => setQuery({ ...query, page })}
      />
    </Box>
  );
};
